import React, { useState, useRef } from 'react';
import { Upload, FileText, File, CheckCircle, AlertCircle, X, Loader2 } from 'lucide-react';

interface ContentUploaderProps {
  onUploadComplete?: (courseId: string, course: any) => void;
  authorId?: string;
}

export const ContentUploader: React.FC<ContentUploaderProps> = ({
  onUploadComplete,
  authorId = 'default-author'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'success' | 'error'>('idle');
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadedCourse, setUploadedCourse] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedFormats = {
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/markdown': 'Markdown',
    'text/plain': 'Text',
    '.pptx': 'PPTX',
    '.md': 'Markdown',
    '.txt': 'Text'
  };

  const getFileType = (file: File): string => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (acceptedFormats[extension as keyof typeof acceptedFormats]) {
      return acceptedFormats[extension as keyof typeof acceptedFormats];
    }
    if (acceptedFormats[file.type as keyof typeof acceptedFormats]) {
      return acceptedFormats[file.type as keyof typeof acceptedFormats];
    }
    return 'Unknown';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    const fileType = getFileType(file);
    if (fileType === 'Unknown') {
      setUploadStatus('error');
      setUploadMessage('Please upload a PPTX, Markdown (.md), or Text (.txt) file');
      return;
    }

    setSelectedFile(file);
    setUploadStatus('idle');
    setUploadMessage('');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const extractPPTX = async (file: File): Promise<any> => {
    // For PPTX files, we need to extract the content
    // This is a simplified version - in production, you'd use a proper PPTX library
    const API_BASE = import.meta.env.VITE_API_BASE || 'https://techtree-lms.rr-startech-innovation.workers.dev';
    const formData = new FormData();
    formData.append('file', file);

    // First, upload the PPTX to get it stored
    const uploadResponse = await fetch(`${API_BASE}/api/content/upload`, {
      method: 'POST',
      body: formData
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload PPTX file');
    }

    const uploadResult = await uploadResponse.json();

    // If it requires extraction (PPTX), we need to handle it client-side
    // In a real implementation, you'd extract the PPTX here or have a separate service
    // For now, we'll simulate the extraction
    setUploadMessage('Processing PPTX file... This may take a moment.');

    // Simulate extraction delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // For demo purposes, create a mock extracted content
    const mockExtractedContent = {
      'slide1.xml': '<xml>Mock slide content</xml>',
      'slide2.xml': '<xml>Mock slide content</xml>'
    };

    // Process the extracted content
    const processResponse = await fetch(`${API_BASE}/api/content/process-pptx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileKey: uploadResult.fileKey,
        xmlContents: mockExtractedContent
      })
    });

    if (!processResponse.ok) {
      throw new Error('Failed to process PPTX content');
    }

    return await processResponse.json();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadStatus('uploading');
    setUploadMessage('Uploading file...');

    try {
      const fileType = getFileType(selectedFile);
      let result;

      if (fileType === 'PPTX') {
        // Handle PPTX extraction
        setUploadStatus('processing');
        result = await extractPPTX(selectedFile);
      } else {
        // Handle Markdown and Text files
        const API_BASE = import.meta.env.VITE_API_BASE || 'https://techtree-lms.rr-startech-innovation.workers.dev';
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('contentType', fileType.toLowerCase());
        formData.append('authorId', authorId);

        const response = await fetch(`${API_BASE}/api/content/upload`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        result = await response.json();
      }

      setUploadStatus('success');
      setUploadMessage('Content uploaded and processed successfully!');
      setUploadedCourse(result.course);

      if (onUploadComplete && result.courseId && result.course) {
        onUploadComplete(result.courseId, result.course);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setUploadMessage(error instanceof Error ? error.message : 'Failed to upload content');
    }
  };

  const resetUploader = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadMessage('');
    setUploadedCourse(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-6">Upload Course Content</h2>

        {/* File Upload Area */}
        {!selectedFile && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">
              Drop your file here or click to browse
            </p>
            <p className="text-sm text-gray-500">
              Supported formats: PPTX, Markdown (.md), or Text (.txt)
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pptx,.md,.txt,text/markdown,text/plain,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        )}

        {/* Selected File Display */}
        {selectedFile && uploadStatus !== 'success' && (
          <div className="border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getFileType(selectedFile) === 'PPTX' && (
                  <File className="w-8 h-8 text-orange-500" />
                )}
                {getFileType(selectedFile) === 'Markdown' && (
                  <FileText className="w-8 h-8 text-purple-500" />
                )}
                {getFileType(selectedFile) === 'Text' && (
                  <FileText className="w-8 h-8 text-blue-500" />
                )}
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {getFileType(selectedFile)} • {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={resetUploader}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Upload Button */}
            <div className="mt-4">
              <button
                onClick={handleUpload}
                disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {uploadStatus === 'uploading' && (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Uploading...
                  </span>
                )}
                {uploadStatus === 'processing' && (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing Content...
                  </span>
                )}
                {uploadStatus === 'idle' && 'Upload and Process'}
              </button>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {uploadMessage && (
          <div className={`mt-4 p-4 rounded-lg flex items-start space-x-3 ${
            uploadStatus === 'error'
              ? 'bg-red-50 text-red-800'
              : uploadStatus === 'success'
              ? 'bg-green-50 text-green-800'
              : 'bg-blue-50 text-blue-800'
          }`}>
            {uploadStatus === 'error' && <AlertCircle className="w-5 h-5 mt-0.5" />}
            {uploadStatus === 'success' && <CheckCircle className="w-5 h-5 mt-0.5" />}
            {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
              <Loader2 className="w-5 h-5 mt-0.5 animate-spin" />
            )}
            <div className="flex-1">
              <p className="font-medium">{uploadMessage}</p>
              {uploadStatus === 'success' && uploadedCourse && (
                <div className="mt-2">
                  <p className="text-sm">Course created: {uploadedCourse.title}</p>
                  <p className="text-sm">Lessons: {uploadedCourse.lessons?.length || 0}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Success Actions */}
        {uploadStatus === 'success' && (
          <div className="mt-6 flex space-x-3">
            <button
              onClick={resetUploader}
              className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
            >
              Upload Another
            </button>
            {uploadedCourse && (
              <button
                onClick={() => window.location.href = `/learn/courses/${uploadedCourse.id}`}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                View Course
              </button>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">How it works:</h3>
          <ol className="text-sm text-gray-600 space-y-1">
            <li>1. Upload your content file (PPTX, Markdown, or Text)</li>
            <li>2. Our system automatically parses and structures the content</li>
            <li>3. Content is organized into lessons and interactive elements</li>
            <li>4. Your e-learning course is ready to share with learners</li>
          </ol>
        </div>
      </div>
    </div>
  );
};